import React, { memo, useCallback, useMemo, useRef, useState } from "react";
import { RCCIcon, RCCDropDown, RCCServicesApi, RCCHookToast, RCCTooltip, RCCHidden } from "react-common-components";
import { useForm } from "react-hook-form";
import { useTranslation } from "src/context/Translations/context";
import { useAuth } from "src/context/Auth/context";
import { ICompany } from "src/context/Auth/interface";
import { v4 } from "uuid";
import LovBusinessGroups from "src/components/Lov/BusinessGroups";
import { useReloadAnimation } from "src/context/ReloadAnimation/context";
import ModalAccessKey from "src/components/ModalAccessKey";
import IRefModalAccessKey from "src/components/ModalAccessKey/interface";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { Link } from "react-router-dom";
import { 
	Container, 
	NavbarContainer,
	NavbarHeader,
	NavbarButtons, 
	MenuDropdownRight,
	DivBusinessGroup,
	DivCompany,
	LiCompanyChange,
	StyledIcon
} from "./styles";
import DashboardsList from "./dashboards";
import changeCompanyLogged from "./changeCompanyLogged";
import Notifications from "../Notifications";

interface IHeader{
	actionFullScreenButton: () => void;
}

const Header: React.FC<IHeader> = ({
	actionFullScreenButton
}) => {
	const { translate } = useTranslation();
	const { REACT_APP_S3_ADDRESS, REACT_APP_S3_DIRECTORY } = process.env;
	const { addToast } = RCCHookToast();
	const { company, businessGroup, updateCompanyLogged, superUser } = useAuth();
	const { control } = useForm();
	const [companies, setCompanies] = useState<ICompany[]>([]);
	const {reload} = useReloadAnimation();
	const { setLoading } = useLoaderBackdrop();
	const modalAccessKey = useRef<IRefModalAccessKey>(null);
	
	const toggleSidebar = () => {
		const root = document.querySelector(":root") as HTMLElement;
		if(root.style.getPropertyValue("--sidebar-width") !== "70px"){
			root.style.setProperty("--sidebar-width", "70px");
			return;
		}
		root.style.removeProperty("--sidebar-width");					
	};

	const changeCompany = useCallback(async (id: number, title: string) => {
		if(superUser){
			if(modalAccessKey.current)
				modalAccessKey.current.openModalAccessKey(id, title, async () => {
					await changeCompanyLogged(id, updateCompanyLogged, reload, translate, addToast, setLoading);
				});

			return;
		}
		await changeCompanyLogged(id, updateCompanyLogged, reload, translate, addToast, setLoading);
	}, [superUser]);

	const loadCompanies = useCallback(async (id: string) => {
		const {data} = await RCCServicesApi.getRequest({
			method: "Business/ListCompanies",
			urlParameters: {
				businessGroupId: id
			}
		});
		setCompanies(data);
	}, []);

	const LovBusinessGroupsElement = useMemo(() => {
		if(!businessGroup && businessGroup === null)
			return null;
		return <LovBusinessGroups 
			control={control}
			name="businessgroup" 
			defaultValue={{ value: businessGroup.efa001Id.toString(), label: businessGroup.efa001Fantasia }}
			onChangeCapture={(e: { value: string; label: string; }) => loadCompanies(e.value)}
		/>;
	}, [businessGroup]);

	const onClickLiMenuBusinessGroups = useCallback(() => {
		if(!companies || companies.length === 0)
			loadCompanies(businessGroup.efa001Id.toString());
	}, [businessGroup, companies]);

	return (
		<>
			<Container>
				<NavbarContainer>
					<StyledIcon 
						name="menu"
						color="var(--purple-lighter)"
						className="toogle-menu"
						size={28}
						onClick={toggleSidebar}
					/>
					<NavbarHeader>
						{company && company !== null && company.efa002Imagem && (
							<img className="company-img-header" src={`${REACT_APP_S3_ADDRESS}/${REACT_APP_S3_DIRECTORY}/imagens_empresas/${company.efa002Imagem}`} alt="Company Logo"/>
						)}
					</NavbarHeader>
					<NavbarButtons>
						<ul>
							{company && company !== null && (
								<>
									{/* Menu Right */}
									<li className="navbar-buttons-li"onClickCapture={onClickLiMenuBusinessGroups}>
										<RCCDropDown
											position="bottomEnd"
											callerComponent={
												<button type="button" className="buttons-toggle">
													<span className="company-name">{company.efa002Fantasia} </span>
													<RCCIcon name="arrowDown" size={30} style={{padding: 0}}/> 
												</button>
											}
											styleInline={`
												>ul{
													>ul{
														top: 0;
														right: -100px;
														@media(max-width: 434px){
															right: -164px;
														}
														@media(max-width: 372px){
															width: 200px;
														}
													}
												}
											`}
										>
											<MenuDropdownRight >
												<DivBusinessGroup onClick={(e) => e.stopPropagation()}>
													<div className="not-active">
														<RCCIcon name="building" />
														<span>
															{translate("Grupo empresarial")}
														</span>
													</div>
														
													{LovBusinessGroupsElement}
														
												</DivBusinessGroup>

												<DivCompany>
													<div className="not-active">
														<RCCIcon name="exchange" />
														<span>
															{translate("Trocar empresa")}  
														</span>
													</div>
													<ul className="company-change">
														{companies.map((companyEl) => (
															<LiCompanyChange 
																key={v4()}
																isSelected={companyEl.efa002Id === company.efa002Id}
															>
																<button 
																	className="button-company-change" 
																	type="button"
																	onClick={() => changeCompany(companyEl.efa002Id, companyEl.efa002Fantasia)}
																>
																	<RCCIcon name="home" />
																	<span>{companyEl.efa002Fantasia}</span>
																</button>
															</LiCompanyChange>
														))}
													</ul>
												</DivCompany>

											</MenuDropdownRight>
										</RCCDropDown>
									</li>
								</>
							)}
						
							{/* Sign out */}
							<li className="navbar-buttons-li">
								{company && company !== null && <DashboardsList /> }
								<RCCTooltip
									color="main"
									text={translate("Calendário")}
									position="bottom"
									styleInline="line-height: 1rem;"
								>
									<Link to="/calendar">
										<button type="button" className="buttons-toggle">
											<RCCIcon name="calendar" size={28}/>
										</button>
									</Link>
								</RCCTooltip>
								<Notifications />
								<RCCHidden xsDown>
									<RCCTooltip
										color="main"
										text={translate("Tela cheia")}
										position="bottom"
										styleInline="line-height: 1rem; left: 35%;"
									>
										<button type="button" className="buttons-toggle" onClick={actionFullScreenButton}>
											<RCCIcon name="arrowsExpand" size={28}/>
										</button>
									</RCCTooltip>
								</RCCHidden>
							</li>
						</ul>
					</NavbarButtons>
				</NavbarContainer>
			</Container>
			<ModalAccessKey 
				ref={modalAccessKey}
			/>
		</>
	);
};

export default memo(Header);
